/**
 * Utility functions for checking business hours and open status
 */

/**
 * Get current time in Swiss timezone (Europe/Zurich)
 * @returns {Date} Current date and time in Swiss timezone
 */
export const getSwissTime = () => {
  return new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Zurich" }));
};

/**
 * Check if the pharmacy is currently open
 * Business hours: Mo-Fr 08:00-19:30 / Sa: 08:00-17:00
 * @param {Function} t - Translation function (optional)
 * @returns {Object} { isOpen: boolean, nextOpenTime: string, status: string }
 */
export const checkPharmacyStatus = (t = null) => {
  const now = getSwissTime();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTime = currentHour * 60 + currentMinute; // Convert to minutes since midnight

  // Business hours in minutes since midnight
  const weekdaysOpen = 8 * 60; // 08:00
  const weekdaysClose = 19 * 60 + 30; // 19:30
  const saturdayOpen = 8 * 60; // 08:00
  const saturdayClose = 17 * 60; // 17:00

  let isOpen = false;
  let nextOpenTime = '';
  let status = '';

  // Check if it's a weekday (Monday to Friday)
  if (dayOfWeek >= 1 && dayOfWeek <= 5) {
    if (currentTime >= weekdaysOpen && currentTime < weekdaysClose) {
      isOpen = true;
      status = t ? t('banner.statusMessages.nowOpen') : 'Jetzt geöffnet';
    } else {
      isOpen = false;
      if (currentTime < weekdaysOpen) {
        nextOpenTime = '08:00';
        status = t ? t('banner.statusMessages.opensAt') : 'Öffnet um 08:00';
      } else {
        // After closing time, next opening is tomorrow at 08:00
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        nextOpenTime = '08:00 morgen';
        status = t ? t('banner.statusMessages.closedOpensTomorrow') : 'Geschlossen - Öffnet morgen um 08:00';
      }
    }
  }
  // Check if it's Saturday
  else if (dayOfWeek === 6) {
    if (currentTime >= saturdayOpen && currentTime < saturdayClose) {
      isOpen = true;
      status = t ? t('banner.statusMessages.nowOpen') : 'Jetzt geöffnet';
    } else {
      isOpen = false;
      if (currentTime < saturdayOpen) {
        nextOpenTime = '08:00';
        status = t ? t('banner.statusMessages.opensAt') : 'Öffnet um 08:00';
      } else {
        // After Saturday closing, next opening is Monday at 08:00
        const daysUntilMonday = (8 - dayOfWeek) % 7; // Calculate days until Monday
        const nextMonday = new Date(now);
        nextMonday.setDate(nextMonday.getDate() + daysUntilMonday);
        nextOpenTime = 'Montag 08:00';
        status = t ? t('banner.statusMessages.closedOpensMonday') : 'Geschlossen - Öffnet Montag um 08:00';
      }
    }
  }
  // Sunday - closed all day
  else {
    isOpen = false;
    const daysUntilMonday = (8 - dayOfWeek) % 7; // Calculate days until Monday
    const nextMonday = new Date(now);
    nextMonday.setDate(nextMonday.getDate() + daysUntilMonday);
    nextOpenTime = 'Montag 08:00';
    status = t ? t('banner.statusMessages.closedOpensMonday') : 'Geschlossen - Öffnet Montag um 08:00';
  }

  return {
    isOpen,
    nextOpenTime,
    status,
    currentTime: now.toLocaleTimeString('de-CH', { 
      timeZone: 'Europe/Zurich',
      hour: '2-digit',
      minute: '2-digit'
    })
  };
};

/**
 * Get formatted business hours string
 * @param {Function} t - Translation function (optional)
 * @returns {string} Formatted business hours
 */
export const getBusinessHours = (t = null) => {
  return t ? t('banner.businessHours') : 'Mo-Fr 08:00-19:30 / Sa: 08:00 – 17:00';
};
